import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock, 
  MapPin, 
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Camera,
  Edit3,
  Import,
  TrendingUp
} from 'lucide-react';

interface ScheduleEntry {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  location?: string;
  title?: string;
  type: 'work' | 'break' | 'meeting';
}

interface NewCalendarProps {
  onNavigateToImport?: () => void;
}

export function NewCalendar({ onNavigateToImport }: NewCalendarProps = {}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [scheduleEntries] = useState<ScheduleEntry[]>([
    {
      id: '1',
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '17:00',
      title: 'Regular Shift',
      type: 'work',
      location: 'Main Office'
    },
    {
      id: '2',
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0], // Tomorrow
      startTime: '10:00',
      endTime: '18:00',
      title: 'Late Shift',
      type: 'work',
      location: 'Downtown Store'
    }
  ]);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const today = new Date().toISOString().split('T')[0];

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const generateCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateString = date.toISOString().split('T')[0];
      const hasSchedule = scheduleEntries.some(entry => entry.date === dateString);
      const isToday = dateString === today;
      const isSelected = dateString === selectedDate;
      
      days.push({
        day,
        date: dateString,
        hasSchedule,
        isToday,
        isSelected
      });
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleDateClick = (dateString: string) => {
    setSelectedDate(selectedDate === dateString ? null : dateString);
  };

  const getSelectedDateEntries = () => {
    if (!selectedDate) return [];
    return scheduleEntries.filter(entry => entry.date === selectedDate);
  };

  const getTotalWeeklyHours = () => {
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    let totalHours = 0;
    scheduleEntries.forEach(entry => {
      const entryDate = new Date(entry.date);
      if (entryDate >= startOfWeek && entryDate <= endOfWeek) {
        const start = new Date(`2000-01-01T${entry.startTime}`);
        const end = new Date(`2000-01-01T${entry.endTime}`);
        totalHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      }
    });
    return totalHours;
  };

  const calendarDays = generateCalendarDays();
  const selectedEntries = getSelectedDateEntries();
  const weeklyHours = getTotalWeeklyHours();

  return (
    <div className="px-4 pb-8 space-y-4 pt-4">
      {/* Professional Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Schedule</h2>
          <p className="text-sm text-muted-foreground">Manage your work calendar</p>
        </div>
        <Button 
          variant="default" 
          size="sm" 
          className="bg-primary hover:bg-primary/90"
          onClick={onNavigateToImport}
        >
          <Import className="w-4 h-4 mr-1" />
          Import
        </Button>
      </div>

      {/* Quick Import Actions */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Button 
          variant="outline" 
          size="sm" 
          className="h-12 flex-col gap-1 text-xs hover:bg-primary/5 border-primary/20"
          onClick={onNavigateToImport}
        >
          <Camera className="w-4 h-4 text-primary" />
          <span className="text-primary font-medium">Photo Import</span>
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          className="h-12 flex-col gap-1 text-xs hover:bg-secondary/80"
          onClick={onNavigateToImport}
        >
          <Edit3 className="w-4 h-4" />
          <span className="font-medium">Manual Entry</span>
        </Button>
      </div>

      {/* Compact Professional Calendar */}
      <Card className="clock-it-card overflow-hidden">
        <CardContent className="p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateMonth('prev')} 
              className="h-8 w-8 p-0 hover:bg-primary/10"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="font-semibold text-foreground text-base">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateMonth('next')} 
              className="h-8 w-8 p-0 hover:bg-primary/10"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Compact Week Days */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Sleek Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayData, index) => (
              <div key={index} className="aspect-square">
                {dayData ? (
                  <button
                    onClick={() => handleDateClick(dayData.date)}
                    className={`w-full h-full rounded-md text-xs font-medium transition-all duration-200 relative flex items-center justify-center ${
                      dayData.isToday
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : dayData.isSelected
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : dayData.hasSchedule
                        ? 'bg-success/10 text-success hover:bg-success/20 border border-success/20'
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {dayData.day}
                    {dayData.hasSchedule && !dayData.isToday && (
                      <div className="absolute bottom-0.5 w-1 h-1 bg-success rounded-full"></div>
                    )}
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Details */}
      {selectedDate && (
        <Card className="clock-it-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground">
                {new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreHorizontal className="w-3 h-3" />
              </Button>
            </div>

            {selectedEntries.length > 0 ? (
              <div className="space-y-3">
                {selectedEntries.map(entry => (
                  <div key={entry.id} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge 
                            variant={entry.type === 'work' ? 'default' : 'secondary'}
                            className={entry.type === 'work' ? 'bg-primary/10 text-primary border-primary/20' : ''}
                          >
                            {entry.type}
                          </Badge>
                          {entry.title && (
                            <span className="font-medium text-foreground text-sm">{entry.title}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>
                              {new Date(`2000-01-01T${entry.startTime}`).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} - {new Date(`2000-01-01T${entry.endTime}`).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                          {entry.location && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3" />
                              <span>{entry.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No schedule for this day</p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs mt-2 text-primary"
                  onClick={onNavigateToImport}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add shift
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Professional Stats Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="clock-it-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">This Week</p>
                <p className="text-xs text-muted-foreground">{weeklyHours}h scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="clock-it-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Avg Weekly</p>
                <p className="text-xs text-muted-foreground">35.2h tracked</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compact Upcoming Events */}
      <Card className="clock-it-card">
        <CardContent className="p-4">
          <h3 className="font-semibold text-foreground mb-3">Upcoming Shifts</h3>
          <div className="space-y-2">
            {scheduleEntries.slice(0, 2).map((entry, index) => (
              <div key={entry.id} className="flex items-center space-x-3 p-2 bg-muted/30 rounded-lg">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                  <Clock className="w-3 h-3 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{entry.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(entry.date).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })} • {new Date(`2000-01-01T${entry.startTime}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })} - {new Date(`2000-01-01T${entry.endTime}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-foreground">
                    {Math.round((new Date(`2000-01-01T${entry.endTime}`).getTime() - new Date(`2000-01-01T${entry.startTime}`).getTime()) / (1000 * 60 * 60))}h
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
